// @ts-strict-ignore
import type { ActivatedRouteSnapshot, Params } from '@angular/router';
import { slashJoin, withLeadingSlash } from 'path-slashes';

import { appRoutePathParams } from 'src/app/routing/app-route-path-params';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { appRouteQueryParams } from 'src/app/routing/app-route-query-params';
import { arraysEqual } from 'src/app/utils/functions/arrays-equal';
import { URL_PATH_SUMMARY } from 'src/app/utils/global-constants';

import type { ActivatedRouteState } from './activated-route-state.interface';

interface ConfigurationDetails {
  isConfiguration: boolean;
  isConfigurationStep: boolean;
  configurationStep: string | null;
  configurationAnchor: string | null;
}

/**
 * @deprecated use router store / router store selectors instead
 */
export function serializeRoute(route: ActivatedRouteSnapshot): ActivatedRouteState {
  route = getDeepestChildRoute(route);

  const segments = getRoutePathSegments(route);
  const path = withLeadingSlash(slashJoin(segments));
  const appRoutePath: AppRoutePath = (Object.values(AppRoutePath) as string[]).includes(segments[0])
    ? (segments[0] as AppRoutePath)
    : null;
  const pathParams: Params = getRoutePathParams(route);
  const isDoorway = appRoutePath === AppRoutePath.Doorway;
  const isPreconfig = appRoutePath === AppRoutePath.Preconfig;
  const isVehicleLength = appRoutePath === AppRoutePath.VehicleLength;
  // TODO: we need to find a better way to ensure, that it's not only a URL starting with motorization, but also a fully valid param set not ending up in 404 page [OWCC-531]
  const isMotorization = appRoutePath === AppRoutePath.Motorization;
  const { isConfiguration, isConfigurationStep, configurationStep, configurationAnchor }: ConfigurationDetails =
    determineConfigurationDetails(route, appRoutePath, pathParams);
  const isSummary = isConfiguration && !!route.data.isSummary;
  const isOnlineCode = appRoutePath === AppRoutePath.OnlineCode;
  let queryParams = getRouteQueryParams(route);

  queryParams = Object.fromEntries(
    Object.keys(queryParams)
      .filter(queryParam => appRouteQueryParams.includes(queryParam))
      .map(queryParam => [queryParam, queryParams[queryParam]])
  );

  return {
    appRoutePath,
    segments,
    path,
    pathParams,
    queryParams,
    isDoorway,
    isPreconfig,
    isVehicleLength,
    isMotorization,
    isConfiguration,
    isConfigurationStep,
    configurationStep,
    configurationAnchor,
    isSummary,
    isOnlineCode
  };
}

export function getDeepestChildRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  return route.firstChild ? getDeepestChildRoute(route.firstChild) : route;
}

export function getRoutePathSegments(route: ActivatedRouteSnapshot): string[] {
  return route.pathFromRoot.reduce<string[]>(
    (segments, snapshot) => [...segments, ...(snapshot.url || []).map(segment => segment.path)],
    []
  );
}

export function getRoutePathParams(route: ActivatedRouteSnapshot): Params {
  let params = { ...route.params };
  while (route.parent) {
    route = route.parent;
    params = { ...route.params, ...params };
  }
  return params;
}

export function getRouteQueryParams(route: ActivatedRouteSnapshot): Params {
  let params = { ...route.queryParams };
  while (route.parent) {
    route = route.parent;
    params = { ...route.queryParams, ...params };
  }
  return params;
}

export function determineConfigurationDetails(
  route: ActivatedRouteSnapshot,
  appRoutePath: AppRoutePath,
  pathParams: Params
): ConfigurationDetails {
  const {
    isConfigurationStep,
    configurationStep
  }: {
    isConfigurationStep: boolean;
    configurationStep: string | null;
  } = determineConfigurationStep(route, appRoutePath, pathParams);

  // It's also configuration when appRoutePathParams for AppRoutePath.ConfigurationStep or AppRoutePath.ConfigurationSubstep match
  const isConfiguration =
    appRoutePath === AppRoutePath.Configuration &&
    (arraysEqual(Object.keys(pathParams), appRoutePathParams[AppRoutePath.Configuration]) || isConfigurationStep);

  // This is for "last level navigation", e.g. to scroll directly to a given section
  const configurationAnchor: string | null =
    isConfigurationStep && !!route.fragment && String(route.fragment).length > 0 ? String(route.fragment) : null;

  return { isConfiguration, isConfigurationStep, configurationStep, configurationAnchor };
}

export function determineConfigurationStep(
  route: ActivatedRouteSnapshot,
  appRoutePath: AppRoutePath,
  pathParams: Params
): { isConfigurationStep: boolean; configurationStep: string | null } {
  const isConfigurationStep =
    appRoutePath === AppRoutePath.Configuration &&
    ((arraysEqual(Object.keys(pathParams), appRoutePathParams[AppRoutePath.ConfigurationStep]) &&
      (pathParams[AppRoutePath.ConfigurationStep] as string).length > 0) ||
      (route.data.isSummary as boolean));

  let configurationStep: string | null = null;

  if (route.data.isSummary) {
    configurationStep = URL_PATH_SUMMARY.toLowerCase();
  } else if (isConfigurationStep) {
    configurationStep = (pathParams[AppRoutePath.ConfigurationStep] as string).toLowerCase();
  }

  return { isConfigurationStep, configurationStep };
}
